import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";

export default function SetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const nav = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (password !== confirm) { toast.error("Passwords do not match."); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/set-password", { token, password });
            setDone(data);
            toast.success("Password set. You may now sign in.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <>
                <Nav variant="light"/>
                <main className="min-h-screen bg-[var(--ebg-ivory)] pt-32 pb-24 text-center">
                    <div className="max-w-md mx-auto px-6" data-testid="set-password-invalid">
                        <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-red-600">Invalid Link</div>
                        <h1 className="font-serif text-4xl mt-4">This link is missing a token.</h1>
                        <p className="text-black/60 mt-4">Please use the exact link that was shared with you by our team.</p>
                        <Link to="/" className="inline-block mt-8 border border-black text-black px-8 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white">Back to Home</Link>
                    </div>
                </main>
                <Footer/>
            </>
        );
    }

    return (
        <>
            <Nav variant="light"/>
            <main className="min-h-screen bg-[var(--ebg-ivory)] pt-32 pb-24">
                <div className="max-w-md mx-auto px-6" data-testid="set-password-page">
                    <div className="bg-white border border-black/8 p-8 md:p-10">
                        <div className="w-14 h-14 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center mb-6">
                            <KeyRound className="text-[var(--ebg-gold)]" size={22}/>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Channel Partner Portal</div>
                        <h1 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Set your<br/>password</h1>
                        <p className="text-black/60 mt-3 text-[13px]">
                            You&apos;ll use these credentials to sign in to the Channel Partner portal at any time.
                        </p>

                        {done ? (
                            <div className="mt-8 p-5 bg-emerald-50 border border-emerald-300 text-emerald-800 text-[13px]" data-testid="set-password-success">
                                <div className="font-bold mb-1">Password set for {done.email}.</div>
                                You can now sign in and access your dashboard.
                                <div className="mt-5">
                                    <button onClick={() => nav("/login?role=partner")} data-testid="go-to-login" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black">
                                        Sign In <ArrowRight size={14}/>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-8 space-y-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-2">New Password</div>
                                    <div className="relative">
                                        <input type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="sp-password" className="w-full bg-white border border-black/15 px-4 py-3 text-[14px] pr-11 focus:border-[var(--ebg-gold)] focus:outline-none" required minLength={8}/>
                                        <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black">
                                            {show ? <EyeOff size={16}/> : <Eye size={16}/>}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-black/55 mb-2">Confirm Password</div>
                                    <input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="sp-confirm" className="w-full bg-white border border-black/15 px-4 py-3 text-[14px] focus:border-[var(--ebg-gold)] focus:outline-none" required minLength={8}/>
                                </div>
                                <div className="text-[11px] text-black/50 flex items-center gap-2 pt-2">
                                    <ShieldCheck size={13} className="text-[var(--ebg-gold)]"/> 8+ characters recommended.
                                </div>
                                <button type="submit" disabled={submitting} data-testid="sp-submit" className="w-full bg-[var(--ebg-gold)] text-black py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-white disabled:opacity-40 inline-flex items-center justify-center gap-2">
                                    {submitting ? <><Loader2 size={14} className="animate-spin"/> Setting…</> : <>Set Password <ArrowRight size={14}/></>}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </main>
            <Footer/>
        </>
    );
}
